import { useState } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FormField } from './form-field.common.styles';
import { useHelperText } from './hooks';

export type IntegerFieldProps<T extends FieldValues> = {
  control: Control<T>;
  path: Path<T>;
  required?: boolean;
  errorMessage?: string;
  errorMessageValues?: Record<string, string | number | undefined>;
  requiredErrorMessage?: string;
  helperText?: string;
  placeholderCopy?: string;
  validate?: (value: string) => boolean;
  'aria-labelledby'?: string;
};

const getValue = (
  value: string,
  display: 'raw' | 'localized',
  locale: string
): string => {
  const cleanValue = value ? value.trim() : '';
  if (!cleanValue || display === 'raw') return cleanValue; //cover the case when value is empty string or whitespace only
  const numericValue = Number(cleanValue);
  if (Number.isNaN(numericValue) || numericValue > Number.MAX_SAFE_INTEGER) {
    return cleanValue;
  } else {
    return numericValue.toLocaleString(locale);
  }
};

const MAX_INTEGER_LENGTH = Number.MAX_SAFE_INTEGER.toString().length;

export const IntegerField = <T extends FieldValues>({
  control,
  path,
  required = false,
  errorMessage,
  errorMessageValues,
  requiredErrorMessage,
  helperText,
  placeholderCopy,
  validate,
  'aria-labelledby': ariaLabelledBy,
}: IntegerFieldProps<T>): React.ReactElement => {
  const { locale, formatMessage } = useIntl();
  const [display, setDisplay] = useState<'raw' | 'localized'>('localized');
  const getHelperText = useHelperText({
    requiredErrorMessage,
    errorMessage,
    errorMessageValues,
    helperText,
  });

  return (
    <Controller
      name={path}
      control={control}
      render={({ field: { value, onBlur, ...fieldProps }, fieldState }) => (
        <FormField
          inputMode="numeric"
          {...fieldProps}
          value={getValue(value, display, locale)}
          onBlur={() => {
            setDisplay('localized');
            onBlur();
          }}
          onFocus={() => {
            setDisplay('raw');
          }}
          placeholder={
            placeholderCopy ? formatMessage({ id: placeholderCopy }) : undefined
          }
          maxLength={MAX_INTEGER_LENGTH}
          error={!!fieldState.error}
          helperText={getHelperText(fieldState.error)}
          aria-labelledby={ariaLabelledBy}
        />
      )}
      rules={{
        required,
        validate,
      }}
    />
  );
};
